import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { useBox, useCallbacks } from 'shared/hooks/utils';
import { useAction, useAppSelector } from 'shared/hooks/redux/hooks';
import { useDeviceConfig } from 'console/shared/contexts/device/config';
import { useDeviceState } from 'console/shared/contexts/device/state';
import { HeatingCircuitControl } from './types';
import { useBooleanState } from 'shared/hooks/state/boolean';
import { selectZ3kTargetTemp } from 'console/redux/z3k-target-temp/selector';
import { selectDevice } from 'entities/device/lib';
export const HeatingCircuitControlProvider = memo(function HeatingCircuitControlProvider(props) {
    const { children, onTargetTemperatureChange, heating_circuit } = props;
    const { device_id, is_legacy_device } = useDeviceConfig();
    const { online } = useDeviceState();
    const device = useAppSelector((state) => selectDevice(state, device_id));
    const { id, target_temperature: initial_target_temperature, min_temperature, max_temperature, is_off_mode, thermometers, summer_mode, } = heating_circuit;
    const current_temperature = thermometers?.target.temperature;
    const z3k_target_temps = useAppSelector(selectZ3kTargetTemp);
    //целевая температура из redux'a, где происходит подтверждение от прибора
    const redux_target_temperature = useMemo(() => z3k_target_temps.by_device_id[device_id]?.[id]?.target_temp ??
        device?.thermostat_target_temps?.[id]?.temp, [z3k_target_temps, device_id, id, device]);
    //получили целевую температуру из СТЕЙТА прибора, если нет температуры из redux'a
    const original_target_temperature = useMemo(() => redux_target_temperature ?? initial_target_temperature, [redux_target_temperature, initial_target_temperature]);
    //Завели стейт для целевой температуры, поскольку её будем менять
    const [target_temperature, setTargetTemperature] = useState(original_target_temperature);
    //Превышена целевая температура?
    const is_violation = useMemo(() => target_temperature < min_temperature ||
        target_temperature > max_temperature, [min_temperature, max_temperature, target_temperature]);
    //сигнализирую о режиме ИЗМЕНЕНИЯ
    const [editing_mode, turnOffEditingMode, turnOnEditingMode] = useBooleanState(false);
    //включаем анимацию вокруг целевой температуры
    //свидетельствует, что отправили команду в прибор и ждём ответ
    const [wait_apply_command, stopWaitApplyCommand, doWaitApplyCommand] = useBooleanState(false);
    //включаем анимацию галочки, что получили подтверждение
    //о применении целевой температуры прибором
    const [success_apply_command, stopShowSuccess, showSuccess] = useBooleanState(false);
    //чтобы первый раз не показывать галочку об успешном применении температуры
    const [first_time_apply_command, offFirstTimeApplyCommand] = useBooleanState(true);
    const target_temperature_off = useMemo(() => {
        if (target_temperature != null) {
            return false;
        }
        const has_target_temperature = target_temperature != null && target_temperature > -30;
        return !has_target_temperature || Boolean(is_off_mode);
    }, [target_temperature, is_off_mode, summer_mode]);
    useEffect(() => {
        //если от прибора пришло подтверждение
        if (redux_target_temperature === undefined && !editing_mode && online) {
            //то выключаем анимацию загрузки
            stopWaitApplyCommand();
            if (!first_time_apply_command) {
                showSuccess();
            }
        }
        return stopWaitApplyCommand;
    }, [redux_target_temperature, editing_mode, online]);
    //Завожу таймаут, по которому отправлю команду на изменение температуры
    const [timeout_id, setTimeoutId] = useState(null);
    const boxed_timeout_id = useBox(timeout_id);
    const sendNewTargetTemperature = useAction(onTargetTemperatureChange);
    const new_target_temperature = useMemo(() => {
        if (target_temperature != null) {
            return target_temperature;
        }
        if (current_temperature != null) {
            return current_temperature;
        }
        return min_temperature;
    }, [target_temperature, min_temperature, current_temperature]);
    const changeTargetTemperature = useCallback(() => sendNewTargetTemperature(new_target_temperature, original_target_temperature), [sendNewTargetTemperature, new_target_temperature]);
    //функция, которая вызовем для изменения температуры
    const sendNewTargetTemp = useCallbacks([
        doWaitApplyCommand,
        offFirstTimeApplyCommand,
        turnOffEditingMode,
        changeTargetTemperature,
    ]);
    //запускаем таймер, если температура изменилась
    useEffect(() => {
        //Если режим редактирования
        if (editing_mode) {
            //Если таймер не был заведён, то создаём
            if (!boxed_timeout_id.current) {
                setTimeoutId(window.setTimeout(sendNewTargetTemp, 2000));
            }
            else {
                //иначе продляем его
                window.clearTimeout(boxed_timeout_id.current);
                setTimeoutId(window.setTimeout(sendNewTargetTemp, 2000));
            }
        }
        return () => {
            if (boxed_timeout_id.current) {
                return window.clearTimeout(boxed_timeout_id.current);
            }
        };
    }, [target_temperature]);
    //Если температура обновилась извне, то обновляем нашу текущую
    useEffect(() => {
        if (target_temperature !== original_target_temperature) {
            setTargetTemperature(original_target_temperature);
        }
    }, [original_target_temperature]);
    useEffect(() => () => {
        if (editing_mode) {
            sendNewTargetTemp();
        }
    }, []);
    let target_fail;
    let target_fail_message;
    if (is_legacy_device) {
        target_fail = device?.thermostat_target_temps?.[id].fail || false;
        target_fail_message =
            device?.thermostat_target_temps?.[id].fail_message ||
                'Произошла ошибка';
    }
    else {
        target_fail = z3k_target_temps.by_device_id[device_id]?.[id]?.fail || false;
        target_fail_message =
            z3k_target_temps.by_device_id[device_id]?.[id]?.fail_message ||
                'Произошла ошибка';
    }
    useEffect(() => {
        if (target_fail) {
            setTargetTemperature(redux_target_temperature);
        }
    }, [target_fail]);
    const heating_circuit_controls = useMemo(() => ({
        setTargetTemperature,
        target_temperature,
        target_temperature_off,
        success_apply_command,
        stopShowSuccess,
        is_violation,
        editing_mode,
        wait_apply_command,
        turnOnEditingMode,
        turnOffEditingMode,
        target_fail,
        target_fail_message,
    }), [
        setTargetTemperature,
        target_temperature,
        target_temperature_off,
        success_apply_command,
        stopShowSuccess,
        is_violation,
        turnOnEditingMode,
        turnOffEditingMode,
        editing_mode,
        wait_apply_command,
        target_fail,
        target_fail_message,
    ]);
    return (_jsx(HeatingCircuitControl.Provider, { value: heating_circuit_controls, children: children }));
});
export const useHeatingCircuitControl = () => useContext(HeatingCircuitControl);
